/**
 * GA4 key events from API results (no content, no ids, no names): huddle started, import started / completed. The
 * wrappers return the same object with a few methods observed, so the session hooks and tokens stay shared.
 * Workspace created, message sent, mail sent and mail imports are observed in container.ts `withAnalytics`.
 */
import type { HuddleApi, SettingsApi } from "@bundle/core";
import type { ImportJob, MailSyncConnection } from "@bundle/contracts";
import type { AnalyticsService, ImportSource } from "./analytics.ts";

/** `huddle_started` when the join answer says I started it (once per huddle). */
export function withHuddleAnalytics(api: HuddleApi, analytics: AnalyticsService, meId: () => string | undefined): HuddleApi {
  const logged = new Set<string>();
  return {
    ...api,
    async join(ws, channelId, req) {
      const res = await api.join(ws, channelId, req);
      const me = meId();
      if (me !== undefined && res.huddle.startedBy === me && !logged.has(res.huddle.id)) {
        logged.add(res.huddle.id);
        analytics.track({ name: "huddle_started" });
      }
      return res;
    },
  };
}

/**
 * Import completion is seen by polling: an id I saw in progress that later reads finished is logged once.
 * Slack/Notion jobs finish at status "done"; a mail connection finishes its import when it moves on to "syncing".
 */
export class ImportWatch {
  private readonly running = new Map<string, ImportSource>();
  private readonly done = new Set<string>();
  constructor(private readonly analytics: AnalyticsService) {}

  started(id: string, source: ImportSource): void {
    this.running.set(id, source);
    this.analytics.track({ name: "import_started", params: { source } });
  }

  jobs(jobs: readonly ImportJob[]): void {
    for (const job of jobs) this.observe(job.id, job.source, job.status === "importing", job.status === "done");
  }

  connections(connections: readonly MailSyncConnection[]): void {
    for (const c of connections) {
      this.observe(c.id, c.kind, c.phase === "connecting" || c.phase === "listing" || c.phase === "importing", c.phase === "syncing");
    }
  }

  private observe(id: string, source: ImportSource, inProgress: boolean, finished: boolean): void {
    if (inProgress) {
      this.running.set(id, source);
    } else if (finished && this.running.has(id) && !this.done.has(id)) {
      this.done.add(id);
      this.running.delete(id);
      this.analytics.track({ name: "import_completed", params: { source } });
    }
  }
}

const watches = new WeakMap<AnalyticsService, ImportWatch>();
/** One watch per analytics service (container), shared by the mail and settings APIs. */
export function importWatch(analytics: AnalyticsService): ImportWatch {
  let watch = watches.get(analytics);
  if (watch === undefined) watches.set(analytics, (watch = new ImportWatch(analytics)));
  return watch;
}

/** Slack/Notion imports (Settings → Import, Settings → Data). */
export function withImportAnalytics(api: SettingsApi, watch: ImportWatch): SettingsApi {
  return {
    ...api,
    async startImport(ws, importId, req) {
      const job = await api.startImport(ws, importId, req);
      watch.started(job.id, job.source);
      return job;
    },
    async importJob(ws, importId) {
      const job = await api.importJob(ws, importId);
      watch.jobs([job]);
      return job;
    },
    async imports(ws) {
      const jobs = await api.imports(ws);
      watch.jobs(jobs);
      return jobs;
    },
    async myImports(ws) {
      const jobs = await api.myImports(ws);
      watch.jobs(jobs);
      return jobs;
    },
  };
}
